import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiVideo, FiVideoOff, FiMic, FiMicOff, FiPhone, FiPhoneOff, FiMaximize, FiMinimize } = FiIcons;

const ScreenShareSession = ({ customerName, onEndSession }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  let mediaStream = null;
  
  // Timer for session duration
  useEffect(() => {
    let interval = null;
    if (isSharing) {
      interval = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    } else if (!isSharing && elapsedTime !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isSharing, elapsedTime]);
  
  // Format elapsed time as HH:MM:SS
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return [
      hrs.toString().padStart(2, '0'),
      mins.toString().padStart(2, '0'),
      secs.toString().padStart(2, '0')
    ].join(':');
  };
  
  const startScreenShare = async () => {
    try {
      // Request screen sharing
      mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: isMicOn
      });
      
      // Set the stream to the video element
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      // Listen for the end of the screen share
      mediaStream.getVideoTracks()[0].onended = () => {
        stopScreenShare();
      };
      
      setIsSharing(true);
      toast.success('Screen sharing started');
    } catch (error) {
      console.error('Error starting screen share:', error);
      toast.error('Failed to start screen sharing');
    }
  };
  
  const stopScreenShare = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    
    setIsSharing(false);
    toast.success('Screen sharing stopped');
  };
  
  const toggleMicrophone = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const audioTracks = videoRef.current.srcObject.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = !isMicOn;
      });
    }
    
    setIsMicOn(!isMicOn);
    toast.success(isMicOn ? 'Microphone muted' : 'Microphone unmuted');
  };
  
  const toggleFullScreen = () => {
    if (!containerRef.current) return;
    
    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.mozRequestFullScreen) {
        containerRef.current.mozRequestFullScreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
      setIsFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullScreen(false);
    }
  };
  
  const handleEndSession = () => {
    stopScreenShare();
    if (onEndSession) {
      onEndSession();
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Screen Sharing Session</h2>
      
      <div className="mb-6">
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <SafeIcon icon={FiVideo} className="h-5 w-5 text-blue-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                {isSharing 
                  ? `Currently sharing your screen with ${customerName}` 
                  : 'Share your screen to let the customer observe the application process'}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div ref={containerRef} className="relative bg-gray-900 rounded-lg overflow-hidden mb-6 h-80">
        <video
          ref={videoRef}
          autoPlay
          className="w-full h-full object-contain"
        />
        
        {!isSharing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <SafeIcon icon={FiVideo} className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-xl font-medium">Click 'Start Sharing' to begin</p>
            </div>
          </div>
        )}
        
        {isSharing && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-50 px-3 py-1 rounded-full">
            <span className="text-red-500 animate-pulse">●</span>
            <span className="text-white text-sm ml-2">Live</span>
          </div>
        )}
        
        {isSharing && (
          <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 px-3 py-1 rounded-full">
            <span className="text-white text-sm">{formatTime(elapsedTime)}</span>
          </div>
        )}
      </div>
      
      <div className="flex flex-wrap justify-center gap-4">
        {isSharing ? (
          <Button
            onClick={stopScreenShare}
            variant="danger"
            className="flex items-center px-6"
          >
            <SafeIcon icon={FiVideoOff} className="mr-2" />
            Stop Sharing
          </Button>
        ) : (
          <Button
            onClick={startScreenShare}
            variant="primary"
            className="flex items-center px-6"
          >
            <SafeIcon icon={FiVideo} className="mr-2" />
            Start Sharing
          </Button>
        )}
        
        <Button
          onClick={toggleMicrophone}
          variant={isMicOn ? "outline" : "warning"}
          className="flex items-center"
        >
          <SafeIcon icon={isMicOn ? FiMic : FiMicOff} className="mr-2" />
          {isMicOn ? 'Mute' : 'Unmute'}
        </Button>
        
        <Button
          onClick={toggleFullScreen}
          variant="outline"
          className="flex items-center"
        >
          <SafeIcon icon={isFullScreen ? FiMinimize : FiMaximize} className="mr-2" />
          {isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
        </Button>
        
        <Button
          onClick={handleEndSession}
          variant="danger"
          className="flex items-center"
        >
          <SafeIcon icon={FiPhoneOff} className="mr-2" />
          End Session
        </Button>
      </div>
      
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiPhone} className="w-5 h-5 text-green-600" />
          <div>
            <p className="font-medium text-gray-900">Can't share screen?</p>
            <p className="text-sm text-gray-600">
              Call the customer directly at: <a href="tel:+919876543210" className="text-primary-600 font-medium">+91 9876543210</a>
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ScreenShareSession;